// Adapter for /api/ai/* endpoints.
//
// Every "interesting" AI endpoint is async on the backend (queued via
// RQ to avoid Heroku's 30s web timeout). The contract is:
//
//   POST /api/ai/<route>     → { status: "queued", job_id }
//   GET  /api/jobs/{job_id}  → { status, progress_pct, current_step, config, error_message }
//
// The result lives in `config.<key>` once the job is `completed`. This
// adapter does the polling (with a WS-free, exponential backoff loop)
// and returns the final payload to the caller — so UI code can write
// straightforward `const pack = await aiApi.generatePack(...)`.

import { apiCall } from "./http";

// ───────────────── Polling primitives ─────────────────

interface JobRow {
  id?: string;
  job_id?: string;
  status?: string;
  progress_pct?: number;
  current_step?: string | null;
  error_message?: string | null;
  config?: Record<string, unknown>;
}

export interface AiProgress {
  pct: number;
  label: string;
  detail?: string;
}

export interface PollOpts {
  onProgress?: (p: AiProgress) => void;
  signal?: AbortSignal;
  pollMs?: number;
  maxMs?: number;
}

const TERMINAL = new Set(["completed", "failed", "cancelled"]);

export class AiJobError extends Error {
  status: string;
  jobId: string;
  constructor(jobId: string, status: string, message: string) {
    super(message);
    this.jobId = jobId;
    this.status = status;
  }
}

async function pollJob(jobId: string, opts: PollOpts = {}): Promise<JobRow> {
  const start = Date.now();
  // URL/PDF/large model calls can run 5+ minutes when the backend has
  // contention. Default to 10 min; callers can override.
  const max = opts.maxMs ?? 10 * 60_000;
  let delay = opts.pollMs ?? 1_500;
  // F-H6 (2026-04-20): we used `apiSoft` here, which swallows ALL
  // non-2xx as `null`. That collapses 401 / 403 / 5xx into the same
  // "consecutive miss" counter — so the user gets a generic
  // "Lost contact with AI worker" after ~12s instead of the real
  // auth or server failure. We now use `apiCall` directly and treat
  // network/parse errors (only) as transient misses; HTTP errors
  // surface immediately with the backend's status text.
  let consecutiveMisses = 0;
  const MAX_MISSES = 6; // ~12s of total fetch failures before we surface.

  while (true) {
    if (opts.signal?.aborted) throw new Error("Aborted");
    if (Date.now() - start > max) throw new AiJobError(jobId, "timeout", "AI job timed out");

    let job: JobRow | null = null;
    try {
      job = await apiCall<JobRow>(`/api/jobs/${jobId}`, { signal: opts.signal });
    } catch (err) {
      // Bubble up real HTTP failures; silently retry pure network
      // hiccups (TypeError "Failed to fetch", AbortError on signal).
      if (opts.signal?.aborted) throw new Error("Aborted");
      if (err instanceof Error && /HTTP \d{3}/.test(err.message)) {
        throw new AiJobError(jobId, "http_error", err.message);
      }
      consecutiveMisses += 1;
      if (consecutiveMisses >= MAX_MISSES) {
        throw new AiJobError(jobId, "lost", "Lost contact with AI worker");
      }
      await sleep(2_000);
      continue;
    }
    if (!job) {
      consecutiveMisses += 1;
      if (consecutiveMisses >= MAX_MISSES) {
        throw new AiJobError(jobId, "lost", "Lost contact with AI worker");
      }
      await sleep(2_000);
      continue;
    }
    consecutiveMisses = 0;
    const status = job.status ?? "queued";
    const pct = typeof job.progress_pct === "number" ? job.progress_pct : 0;
    opts.onProgress?.({ pct, label: job.current_step || statusLabel(status), detail: job.current_step ?? undefined });

    if (TERMINAL.has(status)) {
      if (status !== "completed") {
        throw new AiJobError(jobId, status, job.error_message || `AI job ${status}`);
      }
      return job;
    }
    await sleep(Math.min(delay, 4_000));
    delay = Math.min(delay * 1.25, 4_000);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

function statusLabel(s: string): string {
  if (s === "queued") return "Queued — waiting for AI capacity";
  if (s === "running") return "AI is thinking…";
  return s;
}

async function enqueue<T extends { job_id: string }>(path: string, body?: unknown): Promise<T> {
  return apiCall<T>(path, { method: "POST", body });
}

// ───────────────── Public surface ─────────────────

export interface PackJson {
  company?: { name?: string; industry?: string; description?: string };
  products?: unknown[];
  personas?: unknown[];
  stories?: unknown;
  [k: string]: unknown;
}

export interface SavedPack {
  id: string;
  name: string;
  source?: string;
  status?: string;
}

async function runJobAndExtract<T>(
  start: () => Promise<{ job_id: string }>,
  pluck: (cfg: Record<string, unknown>) => T,
  opts: PollOpts = {},
): Promise<T> {
  const { job_id } = await start();
  const job = await pollJob(job_id, opts);
  return pluck(job.config ?? {});
}

export const aiApi = {
  /** Generate a pack from a free-form description. Returns the raw pack JSON. */
  generatePack: (
    description: string,
    extra: { modelHints?: string[]; autoDetect?: boolean } = {},
    opts: PollOpts = {},
  ) =>
    runJobAndExtract<PackJson>(
      () =>
        enqueue<{ job_id: string }>("/api/ai/generate-pack", {
          description,
          model_hints: extra.modelHints ?? [],
          auto_detect: extra.autoDetect ?? true,
        }),
      (cfg) => (cfg.pack as PackJson) ?? {},
      opts,
    ),

  /** Generate a pack from a public company URL. Backend creates a draft IndustryPack DB row too. */
  generatePackFromUrl: async (
    url: string,
    additionalContext = "",
    opts: PollOpts = {},
  ): Promise<{ pack: PackJson; packId?: string; packName?: string }> => {
    const { job_id } = await enqueue<{ job_id: string }>(
      "/api/ai/generate-pack-from-url",
      { url, additional_context: additionalContext },
    );
    const job = await pollJob(job_id, opts);
    const cfg = job.config ?? {};
    return {
      pack: (cfg.pack as PackJson) ?? {},
      packId: cfg.pack_id as string | undefined,
      packName: cfg.pack_name as string | undefined,
    };
  },

  /** Refine an existing pack using a follow-up instruction. */
  refinePack: async (
    packJson: PackJson,
    instruction: string,
    chatHistory: unknown[] = [],
    opts: PollOpts = {},
  ): Promise<{ pack: PackJson; summary: string }> => {
    const { job_id } = await enqueue<{ job_id: string }>("/api/ai/refine-pack", {
      pack_json: packJson,
      instruction,
      chat_history: chatHistory,
    });
    const job = await pollJob(job_id, opts);
    const cfg = job.config ?? {};
    return {
      pack: (cfg.result_pack as PackJson) ?? packJson,
      summary: (cfg.result_summary as string) ?? "Changes applied.",
    };
  },

  /** Extract a pack from an uploaded PDF. */
  extractPack: async (file: File, opts: PollOpts = {}): Promise<PackJson> => {
    const fd = new FormData();
    fd.append("file", file);
    const { job_id } = await apiCall<{ job_id: string }>("/api/ai/extract-pack", {
      method: "POST",
      rawBody: fd,
    });
    const job = await pollJob(job_id, opts);
    return ((job.config ?? {}).pack as PackJson) ?? {};
  },

  /** Generate a playbook for a saved pack. The pack must have id, products, and stories. */
  generatePlaybook: async (
    packId: string,
    opts: PollOpts = {},
  ): Promise<unknown> => {
    const { job_id } = await enqueue<{ job_id: string }>(
      `/api/packs/${packId}/generate-playbook`,
    );
    const job = await pollJob(job_id, opts);
    const cfg = job.config ?? {};
    return cfg.playbook ?? null;
  },

  /** Tailor a demo plan from a customer brief. */
  demoCoach: async (
    customerContext: string,
    packId = "cirrus_renewable_energy",
    instanceUrl = "",
    opts: PollOpts = {},
  ): Promise<unknown> => {
    const { job_id } = await enqueue<{ job_id: string }>("/api/ai/demo-coach", {
      customer_context: customerContext,
      pack_id: packId,
      instance_url: instanceUrl,
    });
    const job = await pollJob(job_id, opts);
    return ((job.config ?? {}).plan as unknown) ?? null;
  },

  /** Generate a competitive battlecard. */
  battlecard: async (
    competitor: string,
    customerContext = "",
    packId = "cirrus_renewable_energy",
    opts: PollOpts = {},
  ): Promise<unknown> => {
    const { job_id } = await enqueue<{ job_id: string }>("/api/ai/battlecard", {
      competitor,
      customer_context: customerContext,
      pack_id: packId,
    });
    const job = await pollJob(job_id, opts);
    return ((job.config ?? {}).battlecard as unknown) ?? null;
  },

  /** Save a pack JSON to the library as a draft. Returns the new IndustryPack row. */
  savePackDraft: async (pack: PackJson, name?: string): Promise<SavedPack> => {
    const body = {
      name: name || pack.company?.name || "AI-generated pack",
      source: "ai-generated",
      pack_json: pack,
      description: pack.company?.description ?? "",
    };
    return apiCall<SavedPack>("/api/packs", { method: "POST", body });
  },
};

export type AiApi = typeof aiApi;
